import { type ReactNode, FC, useState, useEffect, Fragment } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
// Models
import IInsuranceCase from 'app/models/Case';
import IUser from 'app/models/User';
import IContact from 'app/models/Contact';
import ICaseContact from 'app/models/CaseContact';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getInsuranceCase, updateInsuranceCase } from 'app/store/Cases/Cases.async';
// Selectors
import { selectHcpcsCodesEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectInsuranceCase, selectLoading } from 'app/store/Cases/Cases.selectors';
// Mui
import {
  SxProps, Theme,
  Box, Grid, Stepper, Step, StepLabel
} from '@mui/material';
// Icons
import {
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
// Components
import Dialog from 'app/components/Dialog';
import ClientDetails from 'app/components/ClientDetails';
import { Button } from 'app/components/Mui';
import { Loader } from 'app/components/Utilities';
// Utils
import { convertDateToString, convertTimeToString, parseDate, parseTime } from 'app/utilities/DateTime';
// 
import GeneralStepContent from './GeneralStepContent';
import ClaimantStepContent from './ClaimantStepContent';
import DetailsStepContent from './DetailsStepContent';
import MedicareStepContent from './MedicareStepContent';
import NotificationsStepContent from './NotificationsStepContent';
import OpposingDetailsStepContent from './OpposingDetailsStepContent';
import ContactsStepContent from './ContactsStepContent';

interface IFormData {
  name: string;
  teamId: string | number;
  examinationDate: Dayjs | null;
  trialDate: Dayjs | null;
  settings: {
    medicareOverchargeThreshold: number;
  };
  opposingDetails: {
    opposingCounsel: {
      name: string;
      email: string;
      phone: string;
    },
    caseNumber: string;
    courtJurisdiction: string;
    notes: string;
  };
  claimant: {
    name: string;
    dateOfBirth: Dayjs | null;
  };
  eventDate: Dayjs | null,
  eventTime: Dayjs | null;
  claimNumber: string;
  notificationPreferences: {
    recipientIds: IUser[];
  };
  type: string;
  summary: string;
  expectedOutcome: string;
  contacts: {
    contact: IContact | null;
    role: string;
  }[];
}

type Props = {
  open: boolean;
  onClose: () => void;
  insuranceCaseId?: number;
  disabled?: boolean;
};

const CaseDetailsDialog:FC<Props> = ({
  // Props
  open, onClose, insuranceCaseId, disabled = false,
}) => {
  
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const hcpcsCodesEnabled:boolean = useAppSelector(selectHcpcsCodesEnabled);
  const insuranceCase:IInsuranceCase | null = useAppSelector(selectInsuranceCase);
  const loading:boolean = useAppSelector(selectLoading);

  const steps = [
    t('labels.general'),
    t('labels.claimant'),
    t('labels.details'),
    hcpcsCodesEnabled ? t('labels.medicare') : '',
    t('labels.notifications'),
    t('labels.opposingDetails'),
    t('labels.contacts')
  ].filter(s => s);

  const stepFields:(string[] | null)[] = [
    ['name', 'eventDate', 'eventTime', 'type', 'teamId', 'clientAccountId'],
    ['claimant', 'claimNumber'],
    ['summary', 'expectedOutcome'],
    ['settings'],
    ['notificationPreferences.recipientIds'],
    [],
    ['contacts'],
  ].filter(s => s !== null);

  const [ activeStepIndex, setActiveStepIndex ] = useState(0);

  const methods = useForm<IFormData>({
    defaultValues: {
      name: '',
      teamId: '',
      examinationDate: null,
      trialDate: null,
      settings: {
        medicareOverchargeThreshold: 0
      },
      opposingDetails: {
        opposingCounsel: {
          name: '',
          email: '',
          phone: ''
        },
        caseNumber: '',
        courtJurisdiction: '',
        notes: ''
      },
      claimant: {
        name: '',
        dateOfBirth: null,
      },
      eventDate: null,
      eventTime: null,
      claimNumber: '',
      notificationPreferences: {
        recipientIds: [],
      },
      type: '',
      summary: '',
      expectedOutcome: '',
      contacts: []
    }
  });

  const { reset, handleSubmit, formState:{ errors } } = methods;

  const onSubmit = handleSubmit((data:any) => {
    if ( !insuranceCase ) return;

    const {
      teamId, examinationDate, trialDate, opposingDetails, settings, claimant,
      eventDate, eventTime, claimNumber, notificationPreferences, contacts,
      ...otherData
    } = data;
    const nextData:any = {
      ...otherData,
      opposingDetails: {
        opposingCounsel: {}
      },
      eventDate: convertDateToString(eventDate),
      claimant: {
        name: claimant.name
      },
      version: insuranceCase.version,
      settings: {
        aiChatEnabled: insuranceCase.settings?.aiChatEnabled || false
      }
    };

    if ( claimant.dateOfBirth ) nextData['claimant']['dateOfBirth'] = convertDateToString(claimant.dateOfBirth);

    if ( teamId ) nextData['teamId'] = Number(teamId);

    if ( claimNumber ) nextData['claimNumber'] = claimNumber;

    if ( eventTime ) nextData['eventTime'] = convertTimeToString(eventTime)

    if ( hcpcsCodesEnabled ) {
      nextData['settings']['medicareOverchargeThreshold'] = Number(settings.medicareOverchargeThreshold);
    }

    if ( insuranceCase.notes ) nextData['notes'] = insuranceCase.notes;
    if ( insuranceCase.labels && insuranceCase.labels.length ) nextData['labels'] = insuranceCase.labels;

    if ( examinationDate ) nextData['examinationDate'] = convertDateToString(examinationDate);
    if ( trialDate ) nextData['trialDate'] = convertDateToString(trialDate);

    if (notificationPreferences.recipientIds.length) {
      nextData['notificationPreferences'] = {
        recipientIds: notificationPreferences.recipientIds.map((r:IUser) => r.id)
      }
    }

    if ( contacts && contacts.length ) {
      nextData['contacts'] = contacts.map((contact:ICaseContact) => {
        const contactData:any = { role: contact.role, contactId: contact.contact.id };
        if (contact.id) contactData.id = contact.id;
        return contactData;
      })
    }

    const { opposingCounsel, caseNumber, courtJurisdiction, notes } = opposingDetails;

    if ( caseNumber ) nextData.opposingDetails['caseNumber'] = caseNumber;
    if ( courtJurisdiction ) nextData.opposingDetails['courtJurisdiction'] = courtJurisdiction;
    if ( notes ) nextData.opposingDetails['notes'] = notes;

    Object.keys(opposingCounsel).forEach((key) => {
      if ( opposingCounsel[key] ) nextData.opposingDetails.opposingCounsel[key] = opposingCounsel[key];
    });

    asyncUpdateInsuranceCase({ insuranceCaseId: insuranceCase.id, data: nextData });
  })

  const asyncUpdateInsuranceCase = async (data:any) => {
    try {
      await dispatch(updateInsuranceCase(data)).unwrap();
      onClose();
    } catch(error){}
  }

  useEffect(() => {
    if ( insuranceCaseId ) dispatch(getInsuranceCase(insuranceCaseId));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( !insuranceCase ) return;

    const {
      team, examinationDate, trialDate, claimant, eventDate, eventTime,
      opposingDetails, claimNumber = '', notificationPreferences, contacts,
      ...otherCase
    } = insuranceCase;

    reset({
      ...otherCase,
      claimNumber,
      teamId: team?.id || '',
      examinationDate: examinationDate ? parseDate(examinationDate) : null,
      trialDate: trialDate ? parseDate(trialDate) : null,
      claimant: {
        name: claimant.name,
        dateOfBirth: claimant.dateOfBirth ? parseDate(claimant.dateOfBirth) : null,
      },
      eventDate: eventDate ? parseDate(eventDate) : null,
      eventTime: eventTime ? parseTime(eventTime) : null,
      opposingDetails: {
        opposingCounsel: {
          name: opposingDetails?.opposingCounsel?.name || '',
          email: opposingDetails?.opposingCounsel?.email || '',
          phone: opposingDetails?.opposingCounsel?.phone || ''
        },
        caseNumber: opposingDetails?.caseNumber || '',
        courtJurisdiction: opposingDetails?.courtJurisdiction || '',
        notes: opposingDetails?.notes || ''
      },
      notificationPreferences: {
        recipientIds: notificationPreferences?.recipients || []
      },
      contacts: contacts || []
    });

    // eslint-disable-next-line
  }, [insuranceCase]);

  const hasError = (fields:string[]): boolean => {
    return fields.some(field => {
      const fieldParts = field.split('.');
      let error: any = errors;
      for ( const part of fieldParts ){
        if ( error[part] ){
          error = error[part];
        } else {
          return false;
        }
      }
      return !!error;
    });
  };

  const getStepDisplayStyle = (stepIndex:number) => activeStepIndex === stepIndex ? 'block' : 'none';
  const getStepData = (stepIndex:number):{ icon: ReactNode; sx:SxProps<Theme>; error:boolean } => {
    const active = activeStepIndex === stepIndex;
    const error = hasError(stepFields[stepIndex] as (keyof IFormData)[]);
    return {
      icon: error
        ? <ErrorIcon color="error" />
        : active
          ? <RadioButtonCheckedIcon color="primary" />
          : <RadioButtonUncheckedIcon color="disabled" />
      ,
      sx: {
        cursor: 'pointer !important',
        '& .MuiStepLabel-iconContainer': {
          color: (theme:Theme) => `${theme.palette.primary.main} !important`
        },
        '& .MuiStepLabel-label': {
          color: (theme:Theme) => `${
            error
              ? theme.palette.error.main
              : active
                ? theme.palette.primary.main
                : theme.palette.grey[700]
          }`,
          '&.Mui-completed': {
            fontWeight: 400
          }
        }
      },
      error
    };
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={t('dialogs.caseDetails.title')}
    >
      <FormProvider {...methods}>
        <Box
          sx={{ width: '100%' }}
          component="form"
          onSubmit={onSubmit}
          noValidate
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <Stepper activeStep={activeStepIndex} orientation="vertical">
                {steps.map((label:string, index:number) => {
                  const { icon, sx, error } = getStepData(index);
                  return (
                    <Step
                      key={label}
                      disabled={disabled}
                      onClick={() => setActiveStepIndex(index)}
                    ><StepLabel sx={sx} icon={icon} error={error}>{label}</StepLabel></Step>
                  );
                })}
              </Stepper>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Box sx={{ minHeight: 640 }}>
                {!insuranceCase ? (
                  <Loader />
                ) : (
                  <Fragment>
                    <Box sx={{ display: getStepDisplayStyle(0) }}>
                      <GeneralStepContent disabled={disabled} />
                    </Box>
                    <Box sx={{ display: getStepDisplayStyle(1) }}>
                      <ClaimantStepContent disabled={disabled} />
                    </Box>
                    <Box sx={{ display: getStepDisplayStyle(2) }}>
                      <DetailsStepContent disabled={disabled} />
                    </Box>
                    {hcpcsCodesEnabled ? (
                      <Box sx={{ display: getStepDisplayStyle(3) }}>
                        <MedicareStepContent />
                      </Box>
                    ) : null}
                    <Box sx={{ display: getStepDisplayStyle(steps.length - 3) }}>
                      <NotificationsStepContent />
                    </Box>
                    <Box sx={{ display: getStepDisplayStyle(steps.length - 2) }}>
                      <OpposingDetailsStepContent disabled={disabled} />
                    </Box>
                    <Box sx={{ display: getStepDisplayStyle(steps.length - 1) }}>
                      <ContactsStepContent />
                      <ClientDetails showClaimant={false} showClientAccount={false} insuranceCase={insuranceCase} />
                    </Box>
                  </Fragment>
                )}
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, pt: 4, justifyContent: 'flex-end' }}>
            <Button
              name="Close case details dialog"
              onClick={onClose}
            >{t('buttons.close')}</Button>
            {!disabled && (
              <Button
                name="Save case details dialog"
                loading={loading}
                type="submit"
                variant="contained"
                color="primary"
              >{t('buttons.save')}</Button>
            )}
          </Box>
        </Box>
      </FormProvider>
    </Dialog>
  )
}

export default CaseDetailsDialog;